/**
 * @module NotificationsPage
 * @package @geenius/notifications-solidjs
 * @description Renders the full inbox page for the Tailwind SolidJS variant.
 */

import { FadeIn, motion } from '@geenius/motion/solidjs'
import type {
  Notification,
  NotificationStatus,
} from '@geenius/notifications/shared'
import {
  buildNotificationsSeoMeta,
  getNotificationStatusLabel,
  getNotificationTypeLabel,
  groupNotificationsForDisplay,
  NOTIFICATION_TYPES,
  TYPE_CONFIG,
} from '@geenius/notifications/shared'
import { SEOHead } from '@geenius/seo/solidjs'
import type { JSX } from 'solid-js'
import { createMemo, For, Show } from 'solid-js'
import { EmptyNotifications } from '../components/EmptyNotifications'
import { NotificationItem } from '../components/NotificationItem'
import { createNotificationCopy } from '../lib/notificationCopy'
import {
  getNotificationAccentStyle,
  notificationToggleVariants,
  notificationTypeFilterVariants,
} from '../lib/notificationStyles'
import { cn } from '../lib/utils'
import { createNotifications } from '../primitives/createNotifications'

/**
 * Props for the SolidJS notifications page.
 */
export interface NotificationsPageProps {
  notifications: Notification[] | undefined
  onMarkRead?: (id: string) => Promise<void>
  onMarkAllRead?: () => Promise<void>
  onDismiss?: (id: string) => Promise<void>
  onDelete?: (id: string) => Promise<void>
  class?: string
}

const NOTIFICATION_STATUS_FILTERS: ReadonlyArray<NotificationStatus | 'all'> = [
  'all',
  'unread',
  'read',
]

/**
 * Renders the full notifications inbox.
 *
 * @param props Page props including notification data and mutation handlers.
 * @returns The notifications page markup.
 */
export function NotificationsPage(props: NotificationsPageProps): JSX.Element {
  const { plural, t } = createNotificationCopy()
  const state = createNotifications(() => props.notifications, {
    markRead: props.onMarkRead ?? (async () => {}),
    markAllRead: props.onMarkAllRead ?? (async () => {}),
    dismiss: props.onDismiss ?? (async () => {}),
    deleteNotification: props.onDelete ?? (async () => {}),
  })

  const groupedSections = createMemo(() =>
    Array.from(groupNotificationsForDisplay(state.notifications(), t).entries()),
  )

  const seoMeta = buildNotificationsSeoMeta({
    title: t('notifications.seo.notificationsTitle'),
    description: t('notifications.seo.notificationsDescription'),
    path: '/notifications',
    keywords: ['notifications', 'inbox', 'alerts', 'activity'],
  })

  return (
    <>
      <SEOHead meta={seoMeta} />
      <Show
        when={!state.isLoading()}
        fallback={
          <div class={cn('flex flex-col gap-3 p-6', props.class)}>
            <For each={Array.from({ length: 6 })}>
              {() => (
                <div class="h-16 animate-pulse rounded-xl bg-[var(--gn-notifications-skeleton)]" />
              )}
            </For>
          </div>
        }
      >
        <div class={cn('mx-auto max-w-3xl space-y-6 p-6', props.class)}>
          <FadeIn class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 class="text-2xl font-bold text-foreground">
                {t('notifications.common.title')}
              </h1>
              <p class="mt-1 text-[13px] text-muted-foreground">
                {plural('notifications.common.unreadSummary', state.unreadCount())}
              </p>
            </div>
            <Show when={state.unreadCount() > 0}>
              <motion.button
                type="button"
                onClick={() => props.onMarkAllRead?.()}
                whileHover={{ y: -1, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                class="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-xs font-semibold uppercase tracking-wide text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {t('notifications.common.markAllRead')}
              </motion.button>
            </Show>
          </FadeIn>

          <FadeIn delay={60} class="flex flex-wrap gap-2">
            <For each={NOTIFICATION_STATUS_FILTERS}>
              {(status) => (
                <motion.button
                  type="button"
                  aria-pressed={state.statusFilter() === status}
                  onClick={() => state.setStatusFilter(status)}
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  class={notificationToggleVariants({
                    active: state.statusFilter() === status,
                  })}
                >
                  {getNotificationStatusLabel(status, t)}
                </motion.button>
              )}
            </For>
          </FadeIn>

          <FadeIn delay={120} class="flex flex-wrap gap-1.5">
            <motion.button
              type="button"
              aria-pressed={state.typeFilter() === 'all'}
              onClick={() => state.setTypeFilter('all')}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.98 }}
              class={notificationToggleVariants({
                active: state.typeFilter() === 'all',
              })}
            >
              {t('notifications.filters.typeAll')}
            </motion.button>
            <For each={NOTIFICATION_TYPES}>
              {(type) => (
                <motion.button
                  type="button"
                  aria-pressed={state.typeFilter() === type}
                  onClick={() => state.setTypeFilter(type)}
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  style={getNotificationAccentStyle(type)}
                  class={notificationTypeFilterVariants({
                    active: state.typeFilter() === type,
                  })}
                >
                  {TYPE_CONFIG[type].icon} {getNotificationTypeLabel(type, t)}
                </motion.button>
              )}
            </For>
          </FadeIn>

          <Show when={state.notifications().length > 0} fallback={<EmptyNotifications />}>
            <For each={groupedSections()}>
              {([label, items], index) => (
                <FadeIn delay={160 + index() * 40}>
                  <div class="space-y-2">
                    <h2 class="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                      {label}
                    </h2>
                    <div class="overflow-hidden rounded-xl border border-border">
                      <For each={items}>
                        {(notification) => (
                          <NotificationItem
                            notification={notification}
                            onRead={props.onMarkRead}
                            onDismiss={props.onDismiss}
                          />
                        )}
                      </For>
                    </div>
                  </div>
                </FadeIn>
              )}
            </For>
          </Show>
        </div>
      </Show>
    </>
  )
}
